(function themeMotion(root) {
  'use strict';

  var motionSelector = '[data-motion]';
  var staggerSelector = '[data-motion-stagger]';
  var readyClass = 'js-motion';
  var visibleClass = 'is-motion-visible';
  var defaultStaggerStep = 70;
  var defaultWordStaggerStep = 32;
  var maxStaggerIndex = 8;
  var parallaxSelector = '[data-motion-parallax]';
  var intersectionObserver = null;
  var parallaxElements = [];
  var parallaxFrame = 0;

  function toArray(collection) {
    return Array.prototype.slice.call(collection || []);
  }

  function getMotionElements(scope) {
    var elements = [];

    if (!scope || !scope.querySelectorAll) {
      return elements;
    }

    if (scope.matches && scope.matches(motionSelector)) {
      elements.push(scope);
    }

    return elements.concat(toArray(scope.querySelectorAll(motionSelector)));
  }

  function getStaggerParents(scope) {
    var parents = [];

    if (!scope || !scope.querySelectorAll) {
      return parents;
    }

    if (scope.matches && scope.matches(staggerSelector)) {
      parents.push(scope);
    }

    return parents.concat(toArray(scope.querySelectorAll(staggerSelector)));
  }

  function getMotionElementsFromNode(node) {
    if (!node || node.nodeType !== 1) {
      return [];
    }

    return getMotionElements(node);
  }

  function getUniqueElements(elements) {
    return elements.filter(function filterUnique(element, index, allElements) {
      return allElements.indexOf(element) === index;
    });
  }

  function getPositiveInteger(value, fallback) {
    var numberValue = parseInt(value, 10);

    if (Number.isNaN(numberValue) || numberValue < 0) {
      return fallback;
    }

    return numberValue;
  }

  function prefersReducedMotion() {
    return Boolean(root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function revealElement(element) {
    element.classList.add(visibleClass);
  }

  function revealAllMotionElements(scope) {
    getMotionElements(scope).forEach(revealElement);
  }

  function applyExplicitMotionDelays(scope) {
    getMotionElements(scope).forEach(function applyExplicitMotionDelay(element) {
      var explicitDelay = element.getAttribute('data-motion-delay');

      if ((explicitDelay === null || explicitDelay === '') || element.style.getPropertyValue('--motion-delay')) {
        return;
      }

      element.style.setProperty('--motion-delay', getPositiveInteger(explicitDelay, 0) + 'ms');
    });
  }

  function prepareSplitWords(scope) {
    getMotionElements(scope).forEach(function prepareElement(element) {
      if (element.getAttribute('data-motion-text') !== 'words' || element.hasAttribute('data-motion-prepared')) {
        return;
      }

      var text = (element.textContent || '').replace(/\s+/g, ' ').trim();

      if (!text) {
        return;
      }

      element.setAttribute('data-motion-prepared', 'true');
      element.setAttribute('aria-label', text);
      element.textContent = '';

      text.split(' ').forEach(function appendWord(word, index, words) {
        var wordElement = root.document.createElement('span');
        var delay = getPositiveInteger(element.getAttribute('data-motion-delay'), 0) + (index * defaultWordStaggerStep);

        wordElement.setAttribute('data-motion-word', '');
        wordElement.setAttribute('aria-hidden', 'true');
        wordElement.style.setProperty('--motion-delay', delay + 'ms');
        wordElement.textContent = word;
        element.appendChild(wordElement);

        if (index < words.length - 1) {
          element.appendChild(root.document.createTextNode(' '));
        }
      });
    });
  }

  function applyStaggerDelays(scope) {
    getStaggerParents(scope).forEach(function applyParentStagger(parent) {
      var rawStep = parent.getAttribute('data-motion-stagger');
      var step = rawStep === '' || rawStep === null ? defaultStaggerStep : getPositiveInteger(rawStep, defaultStaggerStep);
      var children = toArray(parent.children).filter(function filterMotionChild(child) {
        return child.matches && child.matches(motionSelector);
      });

      children.forEach(function applyChildDelay(child, index) {
        var explicitDelay = child.getAttribute('data-motion-delay');
        var delay = explicitDelay === null || explicitDelay === ''
          ? Math.min(index, maxStaggerIndex) * step
          : getPositiveInteger(explicitDelay, 0);

        if (!child.style.getPropertyValue('--motion-delay')) {
          child.style.setProperty('--motion-delay', delay + 'ms');
        }
      });
    });
  }

  function getParallaxElements(scope) {
    if (!scope || !scope.querySelectorAll) {
      return [];
    }

    var elements = [];

    if (scope.matches && scope.matches(parallaxSelector)) {
      elements.push(scope);
    }

    return elements.concat(toArray(scope.querySelectorAll(parallaxSelector)));
  }

  function refreshParallaxElements(scope) {
    parallaxElements = getUniqueElements(parallaxElements.concat(getParallaxElements(scope))).filter(function filterConnected(element) {
      return element.isConnected;
    });
  }

  function updateParallax() {
    parallaxFrame = 0;

    if (prefersReducedMotion() || !parallaxElements.length) {
      parallaxElements.forEach(function resetParallax(element) {
        element.style.transform = '';
      });
      return;
    }

    var viewportHeight = root.innerHeight || 1;
    var viewportCenter = viewportHeight / 2;

    parallaxElements.forEach(function updateElement(element) {
      var rect = element.getBoundingClientRect();
      var rawRange = element.getAttribute('data-motion-parallax');
      var range = rawRange === '' || rawRange === null ? 18 : getPositiveInteger(rawRange, 18);
      var elementCenter = rect.top + (rect.height / 2);
      var progress = (viewportCenter - elementCenter) / viewportHeight;
      var offset = Math.max(-range, Math.min(range, progress * range * 2));

      element.style.transform = 'translate3d(0, ' + offset.toFixed(2) + 'px, 0)';
    });
  }

  function scheduleParallaxUpdate() {
    if (parallaxFrame) {
      return;
    }

    parallaxFrame = root.requestAnimationFrame(updateParallax);
  }

  function initParallax(documentReference) {
    refreshParallaxElements(documentReference);

    if (!parallaxElements.length) {
      return;
    }

    updateParallax();
    root.addEventListener('scroll', scheduleParallaxUpdate, { passive: true });
    root.addEventListener('resize', scheduleParallaxUpdate, { passive: true });
  }

  function observeOrReveal(elements) {
    var uniqueElements = getUniqueElements(elements);

    if (!uniqueElements.length) {
      return;
    }

    if (prefersReducedMotion() || !intersectionObserver) {
      uniqueElements.forEach(revealElement);
      return;
    }

    uniqueElements.forEach(function observeElement(element) {
      if (!element.classList.contains(visibleClass)) {
        intersectionObserver.observe(element);
      }
    });
  }

  function createIntersectionObserver() {
    if (!root.IntersectionObserver || prefersReducedMotion()) {
      return null;
    }

    return new root.IntersectionObserver(function handleEntries(entries, observer) {
      entries.forEach(function handleEntry(entry) {
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          revealElement(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.12
    });
  }

  function initMutationObserver(documentReference) {
    if (!root.MutationObserver || !documentReference.body) {
      return;
    }

    new root.MutationObserver(function handleMutations(records) {
      var addedElements = [];

      records.forEach(function handleRecord(record) {
        toArray(record.addedNodes).forEach(function handleNode(node) {
          if (!node || node.nodeType !== 1) {
            return;
          }

          prepareSplitWords(node);
          applyExplicitMotionDelays(node);
          applyStaggerDelays(node.parentElement || node);
          refreshParallaxElements(node);
          addedElements = addedElements.concat(getMotionElementsFromNode(node));
        });
      });

      observeOrReveal(addedElements);
      scheduleParallaxUpdate();
    }).observe(documentReference.body, {
      childList: true,
      subtree: true
    });
  }

  function initThemeMotion(scope) {
    var documentReference = root.document;
    var context = scope || documentReference;

    if (!documentReference || !context) {
      return;
    }

    intersectionObserver = createIntersectionObserver();
    prepareSplitWords(context);
    applyExplicitMotionDelays(context);
    applyStaggerDelays(context);
    initParallax(documentReference);

    // `readyClass` sets opacity 0 on every [data-motion] element, so it must not
    // be applied until the code that reveals them is actually running. It used
    // to be set synchronously while the reveal waited on requestAnimationFrame,
    // which strands content invisible whenever that frame never arrives -- iOS
    // Safari suspends rAF in backgrounded tabs. Anything that hides purchase
    // controls has to fail open.
    root.requestAnimationFrame(function observeInitialElements() {
      documentReference.documentElement.classList.add(readyClass);

      try {
        observeOrReveal(getMotionElements(context));
        initMutationObserver(documentReference);
      } catch (error) {
        revealAllMotionElements(context);
      }
    });

    // Second line of defence: anything already on screen that has not been
    // revealed shortly after load gets revealed anyway. Off-screen elements are
    // left alone so scroll-triggered reveals still behave normally.
    root.setTimeout(function revealStalledMotionElements() {
      var viewportHeight = root.innerHeight || documentReference.documentElement.clientHeight || 0;

      getMotionElements(context).forEach(function revealIfOnScreen(element) {
        if (element.classList.contains(visibleClass)) {
          return;
        }

        var rect = element.getBoundingClientRect();

        if (rect.top < viewportHeight && rect.bottom > 0) {
          revealElement(element);
        }
      });
    }, 1200);

    root.setTimeout(function revealFallback() {
      revealAllMotionElements(context);
    }, 4000);
  }

  if (root.document) {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', function handleReady() {
        initThemeMotion(root.document);
      }, { once: true });
    } else {
      initThemeMotion(root.document);
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      applyStaggerDelays: applyStaggerDelays,
      getMotionElements: getMotionElements,
      initThemeMotion: initThemeMotion,
      prepareSplitWords: prepareSplitWords,
      revealAllMotionElements: revealAllMotionElements,
      revealElement: revealElement
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
